import { PermissionAction } from "../flatbuffers/generated/archive/permission-action";
import type { ScopeState } from "../redux/scope/scopeSlice";

/**
 * The target identifies which entity dimensions to check. Only supplied
 * (non-undefined) dimensions are evaluated; the result is the AND of all
 * supplied dimension checks.
 */
export interface ScopeTarget {
  bankId?: string;
  liftId?: string;
  bankFloorId?: string;
}

/**
 * Check whether a single entity class allows the given action.
 *
 * Invariant: if hasRows is false (no grant rows for this class), the class
 * implicitly allows everything (allow-all). Restriction is explicit opt-in:
 * only when rows exist is a matching {entity_id, action} grant required.
 *
 * @param hasRows   Whether this entity class has any rows in the scope store
 * @param map       The scope map for this entity class
 * @param entityId  The entity UUID to look up
 * @param action    The action to check
 */
function classAllows(
  hasRows: boolean,
  map: Record<string, Set<PermissionAction>>,
  entityId: string | undefined,
  action: PermissionAction,
): boolean {
  if (!hasRows) return true;
  if (!entityId) return true;
  const granted = map[entityId];
  if (!granted) return false;
  return granted.has(action);
}

/**
 * Pure scope permission check. Returns true when the given action is allowed
 * on all supplied target dimensions.
 *
 * Design:
 * - Only dimensions that are supplied (non-undefined) in `target` are evaluated.
 * - Result is the AND of all supplied dimension checks.
 * - empty⇒allow-all per class: if the scope has no rows for a class, that
 *   class always allows (regardless of what target says).
 *
 * No IsMasterAdmin check here — callers should call isMasterAdmin() separately
 * and short-circuit before calling this, or use useEffectivePermission().
 */
export function isScopeAllowed(
  scope: ScopeState,
  action: PermissionAction,
  target: ScopeTarget,
): boolean {
  const bankOk = classAllows(scope.hasBankRows, scope.bankScope, target.bankId, action);
  if (!bankOk) return false;

  const liftOk = classAllows(scope.hasLiftRows, scope.liftScope, target.liftId, action);
  if (!liftOk) return false;

  const floorOk = classAllows(scope.hasFloorRows, scope.floorScope, target.bankFloorId, action);
  return floorOk;
}
